import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { apiGetPublic, apiPost } from "../lib/api";
import { COLORS } from "../lib/colors";

export default function LoginPage() {
  const { sendMagicLink, demoLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("email"); // email | sent | waitlist | waitlist-sent
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  const handleDemoLogin = async () => {
    setDemoLoading(true);
    setError("");
    try {
      await demoLogin();
    } catch (err) {
      console.error("Demo login error:", err);
      setError("Demo login failed. Please try again.");
    } finally {
      setDemoLoading(false);
    }
  };

  // Waitlist form state
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistSchool, setWaitlistSchool] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError("Please enter your email");
      return;
    }

    setError("");
    setSending(true);

    try {
      const data = await apiGetPublic(`/candidates/lookup?email=${encodeURIComponent(trimmed)}`);
      if (!data.exists) {
        // School not on Relay yet — offer waitlist
        setStep("waitlist");
        setSending(false);
        return;
      }

      await sendMagicLink(trimmed);
      setStep("sent");
    } catch (err) {
      console.error("Login error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    setSending(true);
    setError("");
    try {
      await sendMagicLink(email.trim().toLowerCase());
    } catch {
      setError("Failed to resend. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistSchool.trim()) return;
    setSending(true);
    try {
      await apiPost("/waitlist", {
        email: email.trim().toLowerCase(),
        school_name: waitlistSchool.trim(),
        name: waitlistName.trim(),
      });
      setStep("waitlist-sent");
    } catch (err) {
      console.error("Waitlist error:", err);
      // Still show success — don't block the user on a backend error
      setStep("waitlist-sent");
    } finally {
      setSending(false);
    }
  };

  // ── Magic link sent ──────────────────────────────────────────
  if (step === "sent") {
    return (
      <div style={centeredPage}>
        <Logo />
        <div style={{ fontSize: 40, marginBottom: 16 }}>✉</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
          Check your inbox
        </h2>
        <p style={{ fontSize: 14, color: COLORS.textSecondary, marginBottom: 4, maxWidth: 300, textAlign: "center" }}>
          We sent a sign-in link to
        </p>
        <p style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 32 }}>
          {email.trim().toLowerCase()}
        </p>
        <button
          onClick={handleResend}
          disabled={sending}
          style={secondaryBtn(sending)}
        >
          {sending ? "Sending..." : "Resend link"}
        </button>
        <button onClick={() => { setStep("email"); setError(""); }} style={linkBtn}>
          Use a different email
        </button>
      </div>
    );
  }

  // ── Waitlist form ────────────────────────────────────────────
  if (step === "waitlist") {
    return (
      <div style={centeredPage}>
        <Logo />

        <div style={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 16,
          padding: "24px 24px 20px",
          width: "100%",
          maxWidth: 340,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>🏫</div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
            Your school isn't on Relay yet
          </h2>
          <p style={{ fontSize: 13, color: COLORS.textSecondary, lineHeight: 1.5, marginBottom: 20 }}>
            But you can help change that. Leave your details and we'll work on getting your school set up.
          </p>

          <form onSubmit={handleWaitlistSubmit}>
            <label style={labelStyle}>Your name</label>
            <input
              type="text"
              value={waitlistName}
              onChange={(e) => setWaitlistName(e.target.value)}
              placeholder="First name"
              style={{ ...inputStyle, marginBottom: 12 }}
            />

            <label style={labelStyle}>Your university</label>
            <input
              type="text"
              value={waitlistSchool}
              onChange={(e) => setWaitlistSchool(e.target.value)}
              placeholder="e.g. Aalto University"
              required
              autoFocus
              style={{ ...inputStyle, marginBottom: 16 }}
            />

            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>
              📧 We'll use <strong>{email.trim().toLowerCase()}</strong> to follow up.
            </div>

            <button
              type="submit"
              disabled={sending || !waitlistSchool.trim()}
              style={primaryBtn(sending || !waitlistSchool.trim())}
            >
              {sending ? "Sending..." : "Join the waitlist"}
            </button>
          </form>
        </div>

        <button onClick={() => { setStep("email"); setError(""); }} style={linkBtn}>
          ← Try a different email
        </button>
      </div>
    );
  }

  // ── Waitlist confirmed ───────────────────────────────────────
  if (step === "waitlist-sent") {
    return (
      <div style={centeredPage}>
        <Logo />
        <div style={{ fontSize: 40, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
          You're on the list
        </h2>
        <p style={{ fontSize: 14, color: COLORS.textSecondary, maxWidth: 280, textAlign: "center", lineHeight: 1.6, marginBottom: 12 }}>
          We'll reach out to <strong>{waitlistSchool || "your university"}</strong> and let you know when it's live.
        </p>
        <p style={{ fontSize: 13, color: COLORS.textMuted, maxWidth: 260, textAlign: "center", lineHeight: 1.5 }}>
          In the meantime — tell a classmate. The more students from your school who sign up, the faster we move.
        </p>
      </div>
    );
  }

  // ── Email entry (default) ────────────────────────────────────
  return (
    <div style={centeredPage}>
      <Logo />

      <form onSubmit={handleSubmit} style={{ width: "100%", maxWidth: 340 }}>
        <label style={labelStyle}>University email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your.name@university.fi"
          autoComplete="email"
          autoFocus
          style={{ ...inputStyle, border: `1px solid ${error ? COLORS.red : COLORS.border}`, marginBottom: 4 }}
        />

        {error && (
          <p style={{ fontSize: 13, color: COLORS.red, marginTop: 6, marginBottom: 4 }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={sending}
          style={{ ...primaryBtn(sending), marginTop: 12 }}
        >
          {sending ? "Checking..." : "Continue"}
        </button>
      </form>

      <p style={{ marginTop: 32, fontSize: 12, color: COLORS.textMuted, textAlign: "center", maxWidth: 280, lineHeight: 1.5 }}>
        We'll send a sign-in link to your email. No password needed.
      </p>

      <div style={{ marginTop: 32, width: "100%", maxWidth: 340 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <div style={{ flex: 1, height: 1, backgroundColor: COLORS.border }} />
          <span style={{ fontSize: 12, color: COLORS.textMuted, whiteSpace: "nowrap" }}>or</span>
          <div style={{ flex: 1, height: 1, backgroundColor: COLORS.border }} />
        </div>
        <button
          onClick={handleDemoLogin}
          disabled={demoLoading}
          style={{
            width: "100%",
            padding: "11px 0",
            borderRadius: 8,
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface,
            color: COLORS.textSecondary,
            fontSize: 14,
            fontWeight: 500,
            fontFamily: "'DM Sans', sans-serif",
            cursor: demoLoading ? "not-allowed" : "pointer",
            opacity: demoLoading ? 0.6 : 1,
            transition: "opacity 0.15s",
          }}
        >
          {demoLoading ? "Loading demo..." : "Try a demo"}
        </button>
      </div>
    </div>
  );
}

// ── Shared styles ────────────────────────────────────────────

function Logo() {
  return (
    <div style={{ marginBottom: 40, textAlign: "center" }}>
      <div style={{
        width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.accent,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontWeight: 700, fontSize: 22, marginBottom: 12,
      }}>R</div>
      <div style={{ fontWeight: 700, fontSize: 22, color: COLORS.text, marginBottom: 4 }}>Relay</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: "0.12em" }}>
        Student Portal
      </div>
    </div>
  );
}

const centeredPage = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: "40px 24px",
  backgroundColor: COLORS.bg,
};

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 500,
  color: COLORS.textSecondary, marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.surface,
  fontSize: 15,
  fontFamily: "'DM Sans', sans-serif",
  color: COLORS.text,
  outline: "none",
  boxSizing: "border-box",
};

const primaryBtn = (disabled) => ({
  width: "100%",
  padding: "12px 0",
  borderRadius: 8,
  border: "none",
  backgroundColor: COLORS.accent,
  color: "#fff",
  fontSize: 15,
  fontWeight: 600,
  fontFamily: "'DM Sans', sans-serif",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
  transition: "opacity 0.15s",
});

const secondaryBtn = (disabled) => ({
  padding: "10px 24px",
  borderRadius: 8,
  border: `1px solid ${COLORS.border}`,
  backgroundColor: COLORS.surface,
  color: COLORS.textSecondary,
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'DM Sans', sans-serif",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.6 : 1,
});

const linkBtn = {
  marginTop: 12,
  background: "none",
  border: "none",
  color: COLORS.accent,
  fontSize: 13,
  fontFamily: "'DM Sans', sans-serif",
  cursor: "pointer",
};
